import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { AddableCombobox } from '@/components/AddableCombobox';
import { useOptionSet } from '@/lib/useOptionSet';
import { Plus, Trash2, Loader2, Library } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PROJECT_TYPES, BOROUGHS, STATE_OPTIONS, money } from './utils';
import LibraryPicker from './LibraryPicker';

const EMPTY_NF = {
  client_id: '', newClient: false, clientName: '', clientEmail: '', clientPhone: '',
  property_id: '', newProperty: false, propAddress: '', propBorough: '', propBin: '', propBlock: '', propLot: '', projectAddress: '',
  project_type: '', title: '', cover_description: '', cover_template_id: '', payment_terms: '', line_items: [],
  contingency: 0, retainer: 0, surcharge: false,
};

export default function NewProposalDialog({ open, onOpenChange, clients, properties, library, descTemplates, descDefaultId, clientMap, onCreated }) {
  const [nf, setNf] = useState(EMPTY_NF);
  const [submitting, setSubmitting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { options: projectTypeOptions, addOption: addProjectType } = useOptionSet('project_type', PROJECT_TYPES);
  // The Library picker is a nested Radix Dialog. When it unmounts on close, a residual
  // pointer/focus event can trip the parent dialog's "interact outside" dismissal, closing
  // the whole New Proposal form (and losing all entered data). Track when the picker closes
  // and suppress the parent's outside-close during the picker-open window + a short grace
  // period after, while still allowing a genuine outside click to close the form otherwise.
  const pickerClosedAtRef = useRef(0);
  const handlePickerOpenChange = (v) => {
    if (!v) pickerClosedAtRef.current = Date.now();
    setPickerOpen(v);
  };

  useEffect(() => {
    if (open) {
      const def = (descTemplates || []).find(t => t.id === descDefaultId);
      setNf({ ...EMPTY_NF, cover_description: def ? def.content : '', cover_template_id: def ? def.id : '' });
    }
  }, [open, descTemplates, descDefaultId]);

  const applyCoverTemplate = (tid) => {
    const t = (descTemplates || []).find(x => x.id === tid);
    setNf(prev => ({ ...prev, cover_template_id: tid, cover_description: t ? t.content : prev.cover_description }));
  };

  const propertyOptions = () => {
    const list = Array.isArray(properties) ? [...properties] : [];
    const cid = nf.client_id;
    if (cid) list.sort((a, b) => (b.client_id === cid) - (a.client_id === cid));
    return list.map(p => ({ value: p.id, label: `${p.display_name || p.address}${p.borough ? ` : ${p.borough}` : ''}` }));
  };

  const addLibraryItems = (chosen) => {
    setNf(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        ...chosen.map(l => ({ tmp: crypto.randomUUID(), description: l.name, scope_detail: l.description, fee: l.default_fee, item_state: 'included', condition_note: '', library_item_id: l.id })),
      ],
    }));
  };
  const addCustomItem = () => setNf(prev => ({ ...prev, line_items: [...prev.line_items, { tmp: crypto.randomUUID(), description: '', scope_detail: '', fee: 0, item_state: 'included', condition_note: '', library_item_id: null }] }));
  const updateNfItem = (tmp, patch) => setNf(prev => ({ ...prev, line_items: prev.line_items.map(it => it.tmp === tmp ? { ...it, ...patch } : it) }));
  const removeNfItem = (tmp) => setNf(prev => ({ ...prev, line_items: prev.line_items.filter(it => it.tmp !== tmp) }));

  const nfSubtotal = nf.line_items.filter(i => i.item_state === 'included').reduce((s, i) => s + (Number(i.fee) || 0), 0);
  const nfConditional = nf.line_items.filter(i => i.item_state === 'conditional').reduce((s, i) => s + (Number(i.fee) || 0), 0);
  const nfTotal = nfSubtotal + (Number(nf.contingency) || 0);

  const submitNewProposal = async () => {
    if (nf.newClient) { if (!nf.clientName.trim()) return toast.error('Client name is required'); }
    else if (!nf.client_id) return toast.error('Select a client or add a new one');
    if (nf.newProperty && !nf.propAddress.trim()) return toast.error('Property address is required');
    if (nf.newProperty && !nf.propBorough) return toast.error('Property borough is required');
    if (!nf.project_type) return toast.error('Project type is required');
    setSubmitting(true);
    try {
      let clientId = nf.client_id;
      let clientName = clientMap[nf.client_id] || '';
      if (nf.newClient) {
        const cr = await api.post('/clients', { name: nf.clientName.trim(), email: nf.clientEmail, phone: nf.clientPhone });
        clientId = cr.data.id; clientName = cr.data.name;
      }
      let propertyId = nf.newProperty ? null : (nf.property_id || null);
      if (nf.newProperty) {
        const prp = await api.post('/properties', { address: nf.propAddress.trim(), borough: nf.propBorough, bin: nf.propBin, block: nf.propBlock, lot: nf.propLot, client_id: clientId });
        propertyId = prp.data.id;
      }
      const title = nf.title.trim() || `${clientName} - ${nf.project_type}`;
      const line_items = nf.line_items.map((it, i) => ({ number: i + 1, description: it.description, scope_detail: it.scope_detail, fee: Number(it.fee) || 0, item_state: it.item_state || 'included', condition_note: it.condition_note || '', library_item_id: it.library_item_id || null }));
      const pr = await api.post('/proposals', {
        client_id: clientId, title, project_type: nf.project_type,
        property_id: propertyId, property_address: nf.newProperty ? nf.propAddress.trim() : (nf.property_id ? '' : nf.projectAddress.trim()),
        cover_description: nf.cover_description || '', payment_terms: nf.payment_terms || '',
        line_items, contingency_amount: Number(nf.contingency) || 0, retainer_amount: Number(nf.retainer) || 0,
        show_surcharge: nf.surcharge, credit_card_surcharge_pct: 15,
      });
      toast.success('Proposal created');
      onCreated(pr.data, { createdClient: nf.newClient, createdProperty: nf.newProperty });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating proposal'); }
    setSubmitting(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => { if (pickerOpen || Date.now() - pickerClosedAtRef.current < 500) e.preventDefault(); }}
          data-testid="new-proposal-dialog"
        >
          <DialogHeader>
            <DialogTitle style={{ color: '#010A21' }}>New Proposal</DialogTitle>
            <DialogDescription>Create a proposal, adding client and property records as needed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#031333' }}>Client &amp; Property</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Client *</Label>
                  {nf.newClient ? (
                    <div className="space-y-2 mt-1">
                      <Input placeholder="Client Name *" value={nf.clientName} onChange={e => setNf({ ...nf, clientName: e.target.value })} className="bg-background border-border" data-testid="nf-client-name" />
                      <Input placeholder="Email" value={nf.clientEmail} onChange={e => setNf({ ...nf, clientEmail: e.target.value })} className="bg-background border-border" data-testid="nf-client-email" />
                      <Input placeholder="Phone" value={nf.clientPhone} onChange={e => setNf({ ...nf, clientPhone: e.target.value })} className="bg-background border-border" data-testid="nf-client-phone" />
                      <button type="button" className="text-xs text-[#1B4A76] hover:underline" onClick={() => setNf({ ...nf, newClient: false })} data-testid="nf-client-existing">Choose existing client</button>
                    </div>
                  ) : (
                    <div className="mt-1 space-y-1">
                      <SearchableSelect options={(Array.isArray(clients) ? clients : []).map(c => ({ value: c.id, label: c.name }))} value={nf.client_id} onChange={v => setNf({ ...nf, client_id: v, property_id: '' })} placeholder="Select client" searchPlaceholder="Search clients..." emptyText="No clients." testid="nf-client-select" />
                      <button type="button" className="text-xs text-[#1B4A76] hover:underline" onClick={() => setNf({ ...nf, newClient: true, client_id: '' })} data-testid="nf-new-client">+ New client</button>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Property <span className="text-muted-foreground/60">(optional)</span></Label>
                  {nf.newProperty ? (
                    <div className="space-y-2 mt-1">
                      <Input placeholder="Address *" value={nf.propAddress} onChange={e => setNf({ ...nf, propAddress: e.target.value })} className="bg-background border-border" data-testid="nf-prop-address" />
                      <Select value={nf.propBorough || '__none'} onValueChange={v => setNf({ ...nf, propBorough: v === '__none' ? '' : v })}>
                        <SelectTrigger className="bg-background border-border" data-testid="nf-prop-borough"><SelectValue placeholder="Borough *" /></SelectTrigger>
                        <SelectContent className="bg-card border-border">{BOROUGHS.map(b => <SelectItem key={b} value={b} className="text-sm">{b}</SelectItem>)}</SelectContent>
                      </Select>
                      <div className="grid grid-cols-3 gap-2">
                        <Input placeholder="BIN" value={nf.propBin} onChange={e => setNf({ ...nf, propBin: e.target.value })} className="bg-background border-border" data-testid="nf-prop-bin" />
                        <Input placeholder="Block" value={nf.propBlock} onChange={e => setNf({ ...nf, propBlock: e.target.value })} className="bg-background border-border" data-testid="nf-prop-block" />
                        <Input placeholder="Lot" value={nf.propLot} onChange={e => setNf({ ...nf, propLot: e.target.value })} className="bg-background border-border" data-testid="nf-prop-lot" />
                      </div>
                      <button type="button" className="text-xs text-[#1B4A76] hover:underline" onClick={() => setNf({ ...nf, newProperty: false })} data-testid="nf-prop-existing">Choose existing property</button>
                    </div>
                  ) : (
                    <div className="mt-1 space-y-1">
                      <SearchableSelect options={propertyOptions()} value={nf.property_id} onChange={v => setNf({ ...nf, property_id: v })} placeholder="Select property (optional)" searchPlaceholder="Search properties..." emptyText="No properties." testid="nf-property-select" />
                      <button type="button" className="text-xs text-[#1B4A76] hover:underline" onClick={() => setNf({ ...nf, newProperty: true, property_id: '' })} data-testid="nf-new-property">+ New property</button>
                      {!nf.property_id && (
                        <Input value={nf.projectAddress} onChange={e => setNf({ ...nf, projectAddress: e.target.value })} placeholder="Or type a project address (auto-creates a property when you generate the project)" className="bg-background border-border text-sm" data-testid="nf-project-address" />
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Project Type *</Label>
                  <div className="mt-1">
                    <AddableCombobox
                      value={nf.project_type}
                      onChange={(v) => setNf({ ...nf, project_type: v })}
                      options={projectTypeOptions}
                      onCreate={addProjectType}
                      placeholder="Select or add a type"
                      testid="nf-project-type"
                      allowClear={false}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Proposal Title <span className="text-muted-foreground/60">(auto if blank)</span></Label>
                  <Input value={nf.title} onChange={e => setNf({ ...nf, title: e.target.value })} placeholder="Client - Project Type" className="bg-background border-border mt-1" data-testid="nf-title" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Cover Description</Label>
                <Textarea value={nf.cover_description} onChange={e => setNf({ ...nf, cover_description: e.target.value })} rows={5} placeholder="Introductory description shown at the top of the proposal PDF" className="bg-background border-border mt-1 text-sm" data-testid="nf-cover-description" />
                <div className="mt-2 flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground shrink-0">Use template</Label>
                  <Select value={nf.cover_template_id || ''} onValueChange={applyCoverTemplate}>
                    <SelectTrigger className="bg-background border-border h-8 text-xs w-64" data-testid="nf-cover-template-select"><SelectValue placeholder="Select a template" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {(Array.isArray(descTemplates) ? descTemplates : []).map(t => <SelectItem key={t.id} value={t.id} className="text-sm">{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Payment Terms <span className="text-muted-foreground/60">(optional)</span></Label>
                <Textarea value={nf.payment_terms} onChange={e => setNf({ ...nf, payment_terms: e.target.value })} rows={3} placeholder="e.g. 50% due upon signing, balance due upon filing" className="bg-background border-border mt-1 text-sm" data-testid="nf-payment-terms" />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#031333' }}>Scope &amp; Fees</p>
                <div className="flex items-center gap-2">
                  <Button size="sm" className="bg-primary text-primary-foreground" onClick={() => setPickerOpen(true)} data-testid="nf-add-from-library"><Library className="h-3.5 w-3.5 mr-1" /> Add from Library</Button>
                  <Button size="sm" variant="outline" className="border-border" onClick={addCustomItem} data-testid="nf-add-custom"><Plus className="h-3.5 w-3.5 mr-1" /> Add custom item</Button>
                </div>
              </div>
              <div className="border border-border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="w-10 text-xs text-muted-foreground">#</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Description</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Scope Detail</TableHead>
                      <TableHead className="w-28 text-xs text-muted-foreground text-right">Fee</TableHead>
                      <TableHead className="w-32 text-xs text-muted-foreground">State</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nf.line_items.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">No line items. Add from library or add a custom item.</TableCell></TableRow>
                    ) : nf.line_items.map((it, i) => (
                      <TableRow key={it.tmp} className="border-border hover:bg-transparent" style={{ opacity: it.item_state === 'excluded' ? 0.4 : 1 }} data-testid={`nf-item-${i}`}>
                        <TableCell className="text-sm text-muted-foreground">{i + 1}</TableCell>
                        <TableCell><Input value={it.description} onChange={e => updateNfItem(it.tmp, { description: e.target.value })} className="bg-background border-border h-8 text-sm" data-testid={`nf-item-desc-${i}`} /></TableCell>
                        <TableCell><Input value={it.scope_detail} onChange={e => updateNfItem(it.tmp, { scope_detail: e.target.value })} className="bg-background border-border h-8 text-sm" /></TableCell>
                        <TableCell><Input type="number" value={it.fee} onChange={e => updateNfItem(it.tmp, { fee: e.target.value })} className="bg-background border-border h-8 text-sm text-right" style={{ fontFamily: "'DM Mono', monospace" }} data-testid={`nf-item-fee-${i}`} /></TableCell>
                        <TableCell>
                          <Select value={it.item_state} onValueChange={v => updateNfItem(it.tmp, { item_state: v })}>
                            <SelectTrigger className="bg-background border-border h-8 text-xs" data-testid={`nf-item-state-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-card border-border">{STATE_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v} className="text-sm">{l}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell><Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeNfItem(it.tmp)} data-testid={`nf-item-del-${i}`}><Trash2 className="h-4 w-4" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-end">
                <div className="w-72 space-y-2">
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Subtotal (included)</span><span style={{ fontFamily: "'DM Mono', monospace" }} data-testid="nf-subtotal">{money(nfSubtotal)}</span></div>
                  {nfConditional > 0 && <div className="flex items-center justify-between text-xs" style={{ color: '#D97706' }}><span>Additional services if required</span><span style={{ fontFamily: "'DM Mono', monospace" }}>{money(nfConditional)}</span></div>}
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Contingency</span><Input type="number" value={nf.contingency} onChange={e => setNf({ ...nf, contingency: e.target.value })} className="bg-background border-border h-8 w-28 text-right text-sm" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="nf-contingency" /></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Retainer</span><Input type="number" value={nf.retainer} onChange={e => setNf({ ...nf, retainer: e.target.value })} className="bg-background border-border h-8 w-28 text-right text-sm" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="nf-retainer" /></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground text-xs">Credit card surcharge (15%)</span><Switch checked={nf.surcharge} onCheckedChange={v => setNf({ ...nf, surcharge: v })} data-testid="nf-surcharge" /></div>
                  <div className="flex items-center justify-between pt-2 border-t border-border"><span className="font-medium text-sm" style={{ color: '#010A21' }}>Total</span><span className="font-semibold" style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px', color: '#010A21' }} data-testid="nf-total">{money(nfTotal)}</span></div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-border" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button className="bg-primary text-primary-foreground" disabled={submitting} onClick={submitNewProposal} data-testid="nf-submit">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...</> : 'Create Proposal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LibraryPicker open={pickerOpen} onOpenChange={handlePickerOpenChange} library={library} onAdd={addLibraryItems} />
    </>
  );
}
